import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Switch, Route, useHistory } from 'react-router-dom';
import classNames from 'classnames/bind';
import PathriseLogo from './images/pathrise-logo-indigo.svg';
import CloseIcon from './images/close-icon.svg';
import PathriseFellow from './images/rebecca.png';
import ApplyPage from './pages/ApplyPage';
import EnrollmentPage from './pages/EnrollmentPage';
import SlowEnrollmentRedirect from './pages/SlowEnrollmentRedirect';
import SlowEnrollmentError from './pages/SlowEnrollmentError';
import NotFoundPage from './pages/NotFoundPage';
import ScrollToTop from './components/ScrollToTop';
import styles from './styles/App.module.scss';
import { preloadImage, useQuery } from './utils/browserUtils';
import { AppConstants, BASE_PATH } from './utils/constants';
import { trackPageView } from './utils/monitoring';
import { getPageName, handleBrowserNav } from './utils/navHelpers';
import { getData } from './utils/sessionStorage';

const cx = classNames.bind(styles);

const App = () => {
  const [userEmail, setUserEmail] = useState(getData(AppConstants.userEmail));
  const [locationKeys, setLocationKeys] = useState([]);

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const firstUpdate = useRef(true);

  const modalPopupTimeout = useRef(null);
  const query = useQuery();
  const maxBounty =
    query.get('utm_source') === 'maxbounty'
      ? {
          sub2: query.get('sub2'),
        }
      : null;

  const cancelMouseLeave = () => {
    window.clearTimeout(modalPopupTimeout.current);
  };

  const triggerMouseLeave = (e) => {
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    // Only trigger if actually leaving the browser window
    // (Chrome was triggering from hovering auto-fill elements)
    if (
      (clientX < 10 || clientX > innerWidth - 10 || clientY < 10 || clientY > innerHeight - 10) &&
      firstUpdate.current
    ) {
      modalPopupTimeout.current = window.setTimeout(() => {
        firstUpdate.current = false;
        setShowLeaveModal(true);
        document.body.removeEventListener('mouseenter', cancelMouseLeave);
        document.body.removeEventListener('mouseleave', triggerMouseLeave);
      }, 600);
    }
  };

  const closeLeaveModal = () => setShowLeaveModal(false);

  useEffect(() => {
    document.body.addEventListener('mouseenter', cancelMouseLeave);
    document.body.addEventListener('mouseleave', triggerMouseLeave);
    preloadImage(PathriseFellow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const history = useHistory();
  useEffect(() => {
    // subsequent pageViews
    return history.listen((historyLocation) =>
      handleBrowserNav(history, historyLocation, locationKeys, setLocationKeys)
    );
  }, [locationKeys, history]);

  useEffect(() => {
    const shortPath = window.location.pathname.replace(BASE_PATH, '');
    const pageName = getPageName(shortPath);
    trackPageView({ pageName, referrer: document.referrer });
    setLocationKeys([{ key: AppConstants.rootLocationKey, href: window.location.href }]);
  }, []);

  return (
    <>
      <ScrollToTop />
      <main className={styles.AppContainer}>
        <div
          className={cx('popupBackdrop', { visible: showLeaveModal })}
          onClick={closeLeaveModal}
          aria-hidden="true"
        />
        <div className={cx('popup', 'onExitPopup', { visible: showLeaveModal })}>
          <section className={styles.popupContent}>
            <h2>Join fellows like Rebecca</h2>
            <p>In the end, I not only got my dream job, but I had multiple offers!</p>
            <div className={styles.popupFellowInfo}>
              <h1 className={styles.fellowName}>Rebecca Keys</h1>
              <span className={styles.fellowTitle}>PROGRAM MANAGER AT MICROSOFT</span>
            </div>
            <button type="button" onClick={closeLeaveModal}>
              Continue Application
            </button>
          </section>
          <section className={styles.popupImage}>
            <button type="button" className={styles.closeModalBtn} onClick={() => setShowLeaveModal(false)}>
              <img src={CloseIcon} alt="Close" />
            </button>
            <img src={PathriseFellow} className={styles.fellowImage} alt="" />
          </section>
        </div>
        <header className={styles.AppHeader}>
          <div className={styles.headerContainer}>
            <img src={PathriseLogo} className={styles.PathriseLogo} alt="Pathrise" />
            <h3 className={styles.miniTitle}>Application Portal</h3>
          </div>
        </header>
        <Switch>
          <Route exact path="/" render={() => <ApplyPage setUserEmail={setUserEmail} maxBounty={maxBounty} />} />
          <Route path="/s/redirect" component={SlowEnrollmentRedirect} />
          <Route path="/s/error" component={SlowEnrollmentError} />
          <Route
            path="/s/:page"
            render={({ match }) => <EnrollmentPage page={Number(match.params.page)} userEmail={userEmail} slowForm />}
          />
          <Route
            path="/:page"
            render={({ match }) => <EnrollmentPage page={Number(match.params.page)} userEmail={userEmail} />}
          />
          <Route path="*" component={NotFoundPage} />
        </Switch>
      </main>
    </>
  );
};

const AppWrapper = () => {
  return (
    <Router basename={BASE_PATH}>
      <App />
    </Router>
  );
};

export default AppWrapper;
